import { Col, Row } from 'antd';
import { Button } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonFilter from 'components/Button/ButtonFilter';
import ButtonImport from 'components/Button/ButtonImport';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import path from 'routes/Path';
import { icons } from 'utils/constants/icons';
import FilterPaymentSummary from './FilterPaymentSummary';
import TablePaymentSummary from './TablePaymentSummary';
import { useMutation } from '@tanstack/react-query';
import { PaymentSummaryApis } from 'apis/PaymentSummaryApis';
import useQueryParam from 'hooks/useQueryPrams';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { useNavigate } from 'react-router-dom';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import formatNumber, {
  formatCurrency,
  formatDate,
} from 'utils/constants/functions';
import dayjs from 'dayjs';
import { TEAM_TYPE } from 'interfaces/TeamSale/TeamSale';

const PaymentSummaryPage = () => {
  const { user } = useUser();
  const childRef = useRef<any>(null);
  const { PlusOutlined } = icons;
  const navigate = useNavigate();
  const { setTitle, setBreadcrumb } = useTitle();
  const [data, setData] = useState<any>([]);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [formParams, setFormParams] = useState<any>({});
  const [exportLoading, setExportLoading] = useState(false);

  const queryParam = useQueryParam();
  const page = parseInt(queryParam.get('page') + '') || 1;
  const page_size =
    parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const defaultFilter = useMemo(() => {
    const defaultF = {
      // fromDateToDate: [dayjs().month(1).year(2024), dayjs()],
      fromDateToDate: [dayjs().subtract(1, 'month'), dayjs()],
      // statusId: TEAM_TYPE.stay,
    };
    if (
      user?.isSale ||
      user?.isCN ||
      user?.isQTCN ||
      user?.isQLD ||
      user?.isAccountant
    )
      return {
        plantId: (user as any)?.plantCode,
        ...(user?.isSale && { teamSaleId: user?.teamSales?.[0]?.id }),
        ...(user?.isQLD && { managerId: user?.id }),
        ...defaultF,
      };

    if (user?.isAdmin)
      return {
        ...defaultF,
      };
  }, [user]);

  const mutateGetTHTT = useMutation({
    mutationFn: PaymentSummaryApis.getPaymentSummary,
    onSuccess: (res, variables) => {
      // console.log(res);
      const result = res.data?.getBillPaymentList;
      if (variables.type === 'export') {
        // Xử lý dữ liệu trước khi export
        const processedData = result?.items?.map(
          (item: any, index: number) => ({
            ...item,
            index: index + 1,
            // Map loại đội thành tiếng Việt
            teamTypeFormatted: !item.tteamSale?.teamType
              ? 'Kế toán'
              : item.tteamSale.teamType === TEAM_TYPE.stay
                ? 'Tại chỗ'
                : 'Chào hàng',
            // Format các trường số
            marketPriceFormatted: formatNumber(item.marketPrice),
            finalPriceFormatted: formatNumber(item.finalPrice),
            paymentPnjpDebitAmountFormatted: formatNumber(
              item.paymentPnjpDebitAmount,
            ),
            pnjpDebitAmountFormatted: formatNumber(item.pnjpDebitAmount),
            // Format cột KH nợ:Q10 và PNJ nợ:Q10
            paymentCustomerDebitQ10Formatted:
              formatCurrency({
                value: Number(Number(item.paymentCustomerDebitQ10).toFixed(4)),
                format: 4,
              }) || '_',
            paymentPnjpDebitQ10Formatted:
              formatCurrency({
                value: Number(Number(item.paymentPnjpDebitQ10).toFixed(4)),
                format: 4,
              }) || '_',
            // Format ngày tháng
            createDateFormatted: item.createDate
              ? formatDate(item.createDate)
              : '-',
            modifyDateFormatted: item.modifyDate
              ? formatDate(item.modifyDate)
              : '-',
          }),
        );

        // Tạo columns cho export với dữ liệu đã được xử lý
        const exportColumns = [
          { title: 'STT', dataIndex: 'index', width: 50 },
          { title: 'Mã THTT', dataIndex: 'id', width: 100 },
          {
            title: 'Khách hàng',
            dataIndex: ['tcustomer', 'sapCustomerId'],
            width: 150,
          },
          {
            title: 'Tên Khách hàng',
            dataIndex: ['tcustomer', 'storeName'],
            width: 180,
          },
          {
            title: 'Mã KH cha',
            dataIndex: ['tcustomer', 'mainCustomerId'],
            width: 150,
          },
          { title: 'Loại đội', dataIndex: 'teamTypeFormatted', width: 100 },
          { title: 'Mã đội', dataIndex: ['tteamSale', 'subId'], width: 100 },
          { title: 'Tên đội', dataIndex: ['tteamSale', 'name'], width: 150 },
          { title: 'Giá bóng', dataIndex: 'marketPriceFormatted', width: 100 },
          {
            title: 'Tổng thanh toán',
            dataIndex: 'finalPriceFormatted',
            width: 150,
          },
          { title: 'KH ghi nợ', dataIndex: 'customerDebitId', width: 100 },
          {
            title: 'KH nợ:Q10',
            dataIndex: 'paymentCustomerDebitQ10Formatted',
            width: 100,
          },
          {
            title: 'KH nợ: Tiền mặt',
            dataIndex: 'paymentPnjpDebitAmountFormatted',
            width: 170,
          },
          {
            title: 'PNJ nợ: Q10',
            dataIndex: 'paymentPnjpDebitQ10Formatted',
            width: 130,
          },
          {
            title: 'PNJ nợ: Tiền mặt',
            dataIndex: 'pnjpDebitAmountFormatted',
            width: 170,
          },
          { title: 'Chi nhánh', dataIndex: ['tplant', 'id'], width: 100 },
          { title: 'Trạng thái', dataIndex: ['tstatus', 'name'], width: 100 },
          {
            title: 'Người tạo',
            dataIndex: ['tcreateUser', 'fullName'],
            width: 170,
          },
          { title: 'Ngày tạo', dataIndex: 'createDateFormatted', width: 170 },
          {
            title: 'Người cập nhật',
            dataIndex: ['tmodifyUser', 'fullName'],
            width: 170,
          },
          {
            title: 'Ngày cập nhật',
            dataIndex: 'modifyDateFormatted',
            width: 170,
          },
        ];

        exportExcel_v2(
          exportColumns,
          processedData || [],
          'Tổng hợp thanh toán',
        );
      } else {
        setData(result);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  useEffect(() => {
    setTitle('Bảng Tổng hợp thanh toán');
    setBreadcrumb([
      { title: 'Kế toán/Thủ quỹ' },
      { title: 'Bảng Tổng hợp thanh toán' },
    ]);
  }, [setTitle, setBreadcrumb]);

  const refetchGetTHTT = useCallback(
    (isExport?: boolean, headerRow?: any) => {
      try {
        mutateGetTHTT.mutate({
          page: isExport ? 1 : page,
          page_size: isExport ? data?.pageInfo?.totalItems || 1000 : page_size,
          ...(defaultFilter && defaultFilter),
          ...(formParams && formParams),
          ...(isExport && {
            headerRow,
            type: 'export',
          }),
        });
      } catch (error) {
        console.log('mutateGetTHTT', error);
      }
    },
    [page, page_size, formParams, defaultFilter, data?.pageInfo?.totalItems],
  );

  useEffect(() => {
    // console.log('user', user);
    refetchGetTHTT();
  }, [refetchGetTHTT]);

  const handleExportExcel = () => {
    setExportLoading(true);
    if (childRef.current) {
      childRef.current.exportExcel();
    }
    // Reset loading sau khi export xong
    setTimeout(() => {
      setExportLoading(false);
    }, 1000);
  };

  const handleOkFilter = useCallback(
    (values: any) => {
      console.log(values);
      navigate(`?page=1&page_size=${page_size}`);
      setFormParams(values);
    },
    [page_size],
  );

  const handleCancelFilter = useCallback(() => {
    console.log('Cancel Filter');
    setFormParams({});
  }, []);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport
            onClick={handleExportExcel}
            loading={exportLoading}
            disabled={!data?.pageInfo?.totalItems}
          />
        </Col>
        {(user?.isAdmin || user?.isAccountant) && (
          <Col>
            <Button
              fill
              title="Tạo"
              iconBefore={<PlusOutlined />}
              handleOnclick={() =>
                navigate(path.PAYMENT_SUMMARY_DETAIL, {
                  state: {
                    isAdd: true,
                    teamTypeId: TEAM_TYPE.stay,
                  },
                })
              }
            />
          </Col>
        )}
      </Row>

      <FilterPaymentSummary
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        onFinish={handleOkFilter}
        onCancel={handleCancelFilter}
      />
      <TablePaymentSummary
        ref={childRef}
        data={data || {}}
        loading={mutateGetTHTT.isPending}
        refetch={refetchGetTHTT}
      />
    </>
  );
};

export default PaymentSummaryPage;
